import React, { useState } from "react";
import {
  Button, Badge, Modal, ModalHeader, ModalBody, ModalFooter, Input, Label,
} from "reactstrap";

/**
 * Facet filter for the research list.
 *
 * Each facet (symbol / horizon / model) opens its own dialog of checkboxes.
 * Selections OR within a facet and AND across them, so "IONQ or BTC, by GPT-5
 * or Claude, over 1d or 30d" is one combined filter rather than three passes.
 *
 * Selections are staged in the dialog and only applied on Apply, so a half-made
 * choice never triggers a fetch.
 */
const FACETS = [
  { key: "symbols", label: "Symbol", icon: "ri-coin-line" },
  { key: "horizons", label: "Horizon", icon: "ri-time-line" },
  { key: "models", label: "Model", icon: "ri-robot-line" },
  { key: "statuses", label: "Status", icon: "ri-checkbox-circle-line" },
];

/**
 * `extraControls` and `extraChips` let a filter that is not a facet — the date
 * range, whose values are not a list of options to tick — sit in the same row
 * and the same chip strip as the rest, instead of forming a second toolbar.
 */
const FilterBar = ({ facets, selected, onChange, loading, extraControls, extraChips }) => {
  const [openFacet, setOpenFacet] = useState(null);
  const [staged, setStaged] = useState([]);

  const optionsFor = (key) => (facets && facets[key]) || [];
  const labelFor = (key, value) => {
    const match = optionsFor(key).find((o) => o.value === value);
    return match ? match.label : value;
  };

  const openDialog = (key) => {
    setStaged(selected[key] || []);
    setOpenFacet(key);
  };

  const toggleStaged = (value) =>
    setStaged((prev) =>
      prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value]
    );

  const apply = () => {
    onChange({ ...selected, [openFacet]: staged });
    setOpenFacet(null);
  };

  const clearFacet = (key) => onChange({ ...selected, [key]: [] });
  const removeValue = (key, value) =>
    onChange({ ...selected, [key]: (selected[key] || []).filter((v) => v !== value) });
  // Built from FACETS rather than listed, so adding a facet above is the only
  // edit a new filter dimension needs.
  const clearAll = () =>
    onChange(FACETS.reduce((acc, f) => ({ ...acc, [f.key]: [] }), {}));

  const activeCount = FACETS.reduce((n, f) => n + (selected[f.key] || []).length, 0);
  const openFacetMeta = FACETS.find((f) => f.key === openFacet);

  return (
    <>
      <div className="d-flex flex-wrap align-items-center gap-2 mb-3">
        <span className="text-muted fs-13 me-1">Filter:</span>

        {FACETS.map((facet) => {
          const count = (selected[facet.key] || []).length;
          const available = optionsFor(facet.key).length;
          return (
            <Button key={facet.key} size="sm" outline={!count}
                    color={count ? "primary" : "secondary"}
                    disabled={loading || available === 0}
                    onClick={() => openDialog(facet.key)}>
              <i className={`${facet.icon} align-bottom me-1`} />
              {facet.label}
              {count > 0 && <Badge color="light" className="text-primary ms-1">{count}</Badge>}
            </Button>
          );
        })}

        {extraControls}

        {activeCount > 0 && (
          <Button size="sm" color="link" className="text-decoration-none" onClick={clearAll}>
            Clear all
          </Button>
        )}
      </div>

      {(activeCount > 0 || extraChips) && (
        <div className="d-flex flex-wrap align-items-center gap-1 mb-3">
          {extraChips}
          {FACETS.map((facet) =>
            (selected[facet.key] || []).map((value) => (
              <Badge key={`${facet.key}-${value}`} color="primary" className="d-inline-flex align-items-center">
                <span className="opacity-75 me-1">{facet.label}:</span>
                {labelFor(facet.key, value)}
                <button type="button" className="btn-close btn-close-white ms-2"
                        style={{ fontSize: "0.5rem" }}
                        aria-label={`Remove ${labelFor(facet.key, value)}`}
                        onClick={() => removeValue(facet.key, value)} />
              </Badge>
            ))
          )}
        </div>
      )}

      <Modal isOpen={!!openFacet} toggle={() => setOpenFacet(null)} centered>
        <ModalHeader toggle={() => setOpenFacet(null)}>
          Filter by {openFacetMeta ? openFacetMeta.label.toLowerCase() : ""}
        </ModalHeader>
        <ModalBody style={{ maxHeight: 380, overflowY: "auto" }}>
          {optionsFor(openFacet).length === 0 ? (
            <p className="text-muted mb-0">Nothing to filter on yet.</p>
          ) : (
            optionsFor(openFacet).map((option) => (
              <div className="form-check mb-2" key={option.value}>
                <Input className="form-check-input" type="checkbox"
                       id={`opt-${openFacet}-${option.value}`}
                       checked={staged.includes(option.value)}
                       onChange={() => toggleStaged(option.value)} />
                <Label className="form-check-label d-flex justify-content-between w-100"
                       for={`opt-${openFacet}-${option.value}`}>
                  <span>{option.label}</span>
                  <span className="text-muted fs-12">{option.count}</span>
                </Label>
              </div>
            ))
          )}
        </ModalBody>
        <ModalFooter className="justify-content-between">
          <div className="d-flex gap-2">
            <Button size="sm" color="light"
                    onClick={() => setStaged(optionsFor(openFacet).map((o) => o.value))}>
              Select all
            </Button>
            <Button size="sm" color="light" onClick={() => setStaged([])}>None</Button>
          </div>
          <div className="d-flex gap-2">
            <Button color="light" onClick={() => setOpenFacet(null)}>Cancel</Button>
            <Button color="primary" onClick={apply}>
              Apply{staged.length ? ` (${staged.length})` : ""}
            </Button>
          </div>
        </ModalFooter>
      </Modal>
    </>
  );
};

export { FilterBar as default, FACETS };
